import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EstadoError, EstadoVacio, ListaEsqueleto } from '../../components/ui/Estados';
import { Input, Select } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { CampoCompleto, RejillaCampos } from '../../components/layout/RejillaCampos';
import { Tabla, type Columna } from '../../components/ui/Tabla';
import { BarraFiltros } from '../../components/layout/BarraFiltros';
import { CabeceraPagina } from '../../components/layout/CabeceraPagina';
import {
  repositorioTipos,
  type DatosTipoSensor,
  type TipoSensor,
} from '../../../data/repositories/RepositorioTipos';
import { ErrorApp, registrarError } from '../../../domain/errores';
import { useSesionStore } from '../../stores/sesionStore';
import estilos from '../../components/layout/PaginaCrud.module.css';
import { FichaRegistro } from '../../components/layout/FichaRegistro';

/** Tipo con la cuenta de sensores que lo usan. */
interface TipoConUso extends TipoSensor {
  enUso: number;
}

const CAMPOS_VACIOS = {
  nombre: '',
  unidad: '',
  unidadNombre: '',
  esDigital: 'false',
  rapido: 'false',
};

export function TiposSensores() {
  const clienteConsultas = useQueryClient();
  const politica = useSesionStore((s) => s.politica);
  const puedeGestionar = politica?.puede('gestionar_catalogos') ?? false;
  const sinPermiso = puedeGestionar ? null : 'Tu rol no puede modificar los catálogos';

  const [busqueda, setBusqueda] = useState('');

  // Registro cuya ficha de consulta esta abierta.
  const [enFicha, setEnFicha] = useState<TipoConUso | null>(null);
  const [filtroUso, setFiltroUso] = useState<'todos' | 'en_uso' | 'sin_uso'>('todos');
  const [abierto, setAbierto] = useState(false);
  const [enEdicion, setEnEdicion] = useState<TipoConUso | null>(null);
  const [campos, setCampos] = useState({ ...CAMPOS_VACIOS });
  const [error, setError] = useState<string | null>(null);

  const tipos = useQuery({
    queryKey: ['tipos', 'sensores'],
    queryFn: () => repositorioTipos.tiposDeSensor(),
  });

  const uso = useQuery({
    queryKey: ['tipos', 'sensores', 'uso'],
    queryFn: () => repositorioTipos.usoDeTipos(),
  });

  const refrescar = () => {
    void clienteConsultas.invalidateQueries({ queryKey: ['tipos'] });
  };

  const guardar = useMutation({
    mutationFn: (datos: DatosTipoSensor) =>
      enEdicion
        ? repositorioTipos.actualizarTipoSensor(enEdicion.id, datos)
        : repositorioTipos.crearTipoSensor(datos),
    onSuccess: () => {
      toast.success(enEdicion ? 'Tipo actualizado' : 'Tipo creado');
      setAbierto(false);
      setEnEdicion(null);
      refrescar();
    },
    onError: (e) => {
      registrarError('tiposSensores.guardar', e);
      toast.error(e instanceof ErrorApp ? e.message : 'No se pudo guardar el tipo');
    },
  });

  const eliminar = useMutation({
    mutationFn: (id: number) => repositorioTipos.eliminarTipoSensor(id),
    onSuccess: () => {
      toast.success('Tipo eliminado');
      refrescar();
    },
    onError: (e) => {
      registrarError('tiposSensores.eliminar', e);
      toast.error(e instanceof ErrorApp ? e.message : 'No se pudo eliminar el tipo');
    },
  });

  const lista = useMemo<TipoConUso[]>(
    () => (tipos.data ?? []).map((t) => ({ ...t, enUso: uso.data?.get(t.id) ?? 0 })),
    [tipos.data, uso.data],
  );

  const filtrados = useMemo(
    () =>
      lista.filter((t) => {
        if (filtroUso === 'en_uso' && t.enUso === 0) return false;
        if (filtroUso === 'sin_uso' && t.enUso > 0) return false;

        const q = busqueda.trim().toLowerCase();
        return (
          !q || `${t.nombre} ${t.unidad ?? ''} ${t.unidadNombre ?? ''}`.toLowerCase().includes(q)
        );
      }),
    [lista, busqueda, filtroUso],
  );

  const abrirNuevo = () => {
    setEnEdicion(null);
    setCampos({ ...CAMPOS_VACIOS });
    setError(null);
    setAbierto(true);
  };

  const abrirEdicion = (tipo: TipoConUso) => {
    setEnEdicion(tipo);
    setCampos({
      nombre: tipo.nombre,
      unidad: tipo.unidad ?? '',
      unidadNombre: tipo.unidadNombre ?? '',
      esDigital: String(tipo.esDigital),
      rapido: String(tipo.rapido),
    });
    setError(null);
    setAbierto(true);
  };

  const enviar = () => {
    setError(null);

    if (!campos.nombre.trim()) {
      setError('Indica el nombre del tipo.');
      return;
    }

    const esDigital = campos.esDigital === 'true';

    if (!campos.unidadNombre.trim() || !campos.unidad.trim()) {
      setError(
        esDigital
          ? 'Indica cómo se llama el estado y su abreviatura (por ejemplo «Estado» y «on/off»).'
          : 'Un sensor de medida necesita el nombre de la unidad y su símbolo (Grados Celsius, °C).',
      );
      return;
    }

    guardar.mutate({
      nombre: campos.nombre.trim(),
      unidad_nombre: campos.unidadNombre.trim(),
      unidad_simbolo: campos.unidad.trim(),
      es_digital: esDigital,
      // La velocidad sólo tiene sentido en los analógicos.
      tipo: esDigital ? false : campos.rapido === 'true',
    });
  };

  const columnas: Columna<TipoConUso>[] = [
    {
      clave: 'nombre',
      encabezado: 'Tipo',
      movil: 'titulo',
      render: (t) => <span className={estilos.nombre}>{t.nombre}</span>,
    },
    {
      clave: 'naturaleza',
      encabezado: 'Qué reporta',
      movil: 'subtitulo',
      render: (t) =>
        t.esDigital ? (
          <Badge color="var(--status-info)">Encendido / apagado</Badge>
        ) : (
          <Badge color="var(--text-2)">Valor numérico</Badge>
        ),
    },
    { clave: 'unidad', encabezado: 'Unidad', render: (t) => t.unidad ?? '—' },
    {
      clave: 'uso',
      encabezado: 'Sensores que lo usan',
      movil: 'detalle',
      render: (t) =>
        t.enUso === 0 ? <span className={estilos.secundario}>Ninguno</span> : t.enUso,
    },
    {
      clave: 'acciones',
      encabezado: 'Acciones',
      alinear: 'centro',
      movil: 'accion',
      render: (t) => (
        <div className={estilos.acciones}>
          <Button
            tamano="sm"
            variante="secundario"
            icono="ver"
            title="Ver datos"
            onClick={() => setEnFicha(t)}
          />
          <Button
            tamano="sm"
            variante="secundario"
            icono="editar"
            title="Editar"
            motivoDeshabilitado={sinPermiso}
            onClick={() => abrirEdicion(t)}
          />
          <Button
            tamano="sm"
            variante="secundario"
            icono="eliminar"
            title="Eliminar"
            motivoDeshabilitado={
              sinPermiso ?? (t.enUso > 0 ? `No se puede borrar: lo usan ${t.enUso} sensores` : null)
            }
            onClick={() => {
              if (window.confirm(`¿Eliminar el tipo «${t.nombre}»?`)) eliminar.mutate(t.id);
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div className={estilos.pagina}>
      <CabeceraPagina
        titulo="Tipos de sensores"
        descripcion="Catálogo de qué puede medir un sensor y en qué unidad."
        acciones={
          <>
            <Button
              variante="secundario"
              icono="refrescar"
              compactoEnMovil
              onClick={refrescar}
              cargando={tipos.isFetching}
            >
              Actualizar
            </Button>
            <Button icono="agregar" motivoDeshabilitado={sinPermiso} onClick={abrirNuevo}>
              Nuevo tipo
            </Button>
          </>
        }
      />

      <BarraFiltros>
        <Input
          etiqueta="Buscar"
          icono="buscar"
          placeholder="Nombre o unidad"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
        <Select
          etiqueta="Uso"
          value={filtroUso}
          onChange={(e) => setFiltroUso(e.target.value as typeof filtroUso)}
          opciones={[
            { valor: 'todos', etiqueta: 'Todos' },
            { valor: 'en_uso', etiqueta: 'En uso' },
            { valor: 'sin_uso', etiqueta: 'Sin usar' },
          ]}
        />
      </BarraFiltros>

      {tipos.isLoading ? (
        <ListaEsqueleto filas={5} />
      ) : tipos.isError ? (
        <EstadoError
          acciones={
            <Button variante="secundario" icono="refrescar" onClick={refrescar}>
              Reintentar
            </Button>
          }
        />
      ) : filtrados.length === 0 ? (
        <EstadoVacio
          icono="categoria"
          titulo={lista.length === 0 ? 'Sin tipos de sensor' : 'Ninguno coincide'}
          descripcion={
            lista.length === 0
              ? 'Crea un tipo para poder dar de alta sensores que lo usen.'
              : 'Prueba con otra búsqueda.'
          }
        />
      ) : (
        <>
          <Tabla
            datos={filtrados}
            columnas={columnas}
            claveFila={(t) => t.id}
            etiqueta="Tipos de sensores"
          />

          <FichaRegistro
            item={enFicha}
            titulo="Tipo de sensor"
            columnas={columnas}
            onCerrar={() => setEnFicha(null)}
          />
        </>
      )}

      <Modal
        abierto={abierto}
        onCerrar={() => setAbierto(false)}
        titulo={enEdicion ? 'Editar tipo de sensor' : 'Nuevo tipo de sensor'}
        pie={
          <>
            <Button variante="secundario" onClick={() => setAbierto(false)}>
              Cancelar
            </Button>
            <Button onClick={enviar} cargando={guardar.isPending} icono="guardar">
              Guardar
            </Button>
          </>
        }
      >
        <div className={estilos.formulario}>
          {error && <div className={estilos.error}>{error}</div>}

          <RejillaCampos>
            <Input
              etiqueta="Nombre"
              requerido
              placeholder="Temperatura, Contacto magnético…"
              value={campos.nombre}
              onChange={(e) => setCampos((c) => ({ ...c, nombre: e.target.value }))}
            />

            <Select
              etiqueta="Qué reporta"
              value={campos.esDigital}
              onChange={(e) => setCampos((c) => ({ ...c, esDigital: e.target.value }))}
              ayuda="Un valor numérico se compara con un rango; un estado, con el valor esperado"
              opciones={[
                { valor: 'false', etiqueta: 'Un valor numérico' },
                { valor: 'true', etiqueta: 'Encendido o apagado' },
              ]}
            />

            {/*
              Los dos campos de unidad son obligatorios en la base, también para
              los sensores de estado. Se piden siempre, con el texto adaptado para
              que en un sensor de encendido/apagado se entienda qué poner. Van uno
              al lado del otro porque son el mismo dato escrito de dos maneras.
            */}
            <Input
              etiqueta="Nombre de la unidad"
              requerido
              placeholder={campos.esDigital === 'true' ? 'Estado' : 'Grados Celsius, Porcentaje…'}
              value={campos.unidadNombre}
              onChange={(e) => setCampos((c) => ({ ...c, unidadNombre: e.target.value }))}
              ayuda="Cómo se llama, escrito entero"
            />

            <Input
              etiqueta="Símbolo"
              requerido
              placeholder={campos.esDigital === 'true' ? 'on/off' : '°C, %, V, A, bar…'}
              value={campos.unidad}
              onChange={(e) => setCampos((c) => ({ ...c, unidad: e.target.value }))}
              ayuda="Abreviatura que acompaña al valor en pantalla"
            />

            {campos.esDigital === 'false' && (
              <CampoCompleto>
                <Select
                  etiqueta="Velocidad de lectura"
                  value={campos.rapido}
                  onChange={(e) => setCampos((c) => ({ ...c, rapido: e.target.value }))}
                  ayuda="Rápido para magnitudes que cambian de golpe; lento para las que apenas varían"
                  opciones={[
                    { valor: 'false', etiqueta: 'Lenta' },
                    { valor: 'true', etiqueta: 'Rápida' },
                  ]}
                />
              </CampoCompleto>
            )}
          </RejillaCampos>
        </div>
      </Modal>
    </div>
  );
}
